import React from "react";
import { Box, Paper, Stack, Typography } from "@mui/material";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppButton } from "../form";
import authService from "../../services/authService";

type FallbackProps = {
  error: Error | null;
  onReset?: () => void;
};

export function UnexpectedErrorFallback({ error, onReset }: FallbackProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    onReset?.();
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  const handleLogout = () => {
    onReset?.();
    void authService.logout();
    navigate("/logout");
  };

  return (
    <Box
      sx={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: 4,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 520,
          width: "100%",
          p: { xs: 3, sm: 4 },
          border: (theme) => `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2} alignItems="flex-start">
          <ErrorOutlineOutlinedIcon color="error" sx={{ fontSize: 40 }} />
          <Typography variant="h5" fontWeight={700}>
            {t("error.unexpectedTitle")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("error.unexpectedBody")}
          </Typography>
          {error?.message && (
            <Typography
              variant="body2"
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                width: "100%",
                maxHeight: 160,
                overflow: "auto",
                bgcolor: "action.hover",
                borderRadius: 1,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              }}
            >
              {error.message}
            </Typography>
          )}
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1, width: "100%" }}>
            <AppButton variant="outlined" onClick={handleBack} fullWidth>
              {t("error.back")}
            </AppButton>
            <AppButton variant="contained" color="error" onClick={handleLogout} fullWidth>
              {t("error.logout")}
            </AppButton>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

type BoundaryProps = {
  children: React.ReactNode;
  /** Reset boundary when this key changes (e.g. route pathname). */
  resetKey?: string;
};

type BoundaryState = {
  error: Error | null;
};

/**
 * Catches unexpected render/runtime errors and shows logout + back actions.
 * Business API exceptions continue to use toast/snackbar messaging.
 */
export default class AppErrorBoundary extends React.Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("Unexpected UI error:", error, info.componentStack);
  }

  componentDidUpdate(prevProps: BoundaryProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({ error: null });
    }
  }

  private reset = () => {
    this.setState({ error: null });
  };

  render() {
    if (this.state.error) {
      return <UnexpectedErrorFallback error={this.state.error} onReset={this.reset} />;
    }
    return this.props.children;
  }
}
